import { AloftPrice, AloftPriceJSON } from '@aloft/shared-objects';
import { schAloftPrice } from '@aloft/toolkit-shared';
import { Input, theme } from '@aloft/toolkit-sui';

import { Controller, ControllerRenderProps, FieldPath, useFormContext } from 'react-hook-form';

import { selectCompAdjustments } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { PropertyGridFieldProps } from './types.ts';
import { getAdjustmentColor, getCompAdjustmentValue } from './utils.tsx';

export const AdjustmentField = <T, U extends string | number>({
  columnIndex,
  field,
}: PropertyGridFieldProps<T, U>) => {
  const dispatch = useAppDispatch();
  const { manualAdjustmentKey } = field;

  // If a field doesn't have a manualAdjustmentKey, render a disabled input
  if (!manualAdjustmentKey) {
    return (
      <Input
        type="text"
        value=""
        isDisabled
        onChange={() => {}}
        onClear={() => {}}
        __dangerDoNotUseSx={{
          height: '100%',
          padding: '4px 8px',
          border: '1px solid transparent',
          outline: 'none',
          borderRadius: '0',
          fontSize: '14px',
          textAlign: 'right',
          backgroundColor: theme.colors.textOffset,
        }}
      />
    );
  }

  const fieldName =
    `columns.${columnIndex}.propertyData.editableData.manualAdjustments.${manualAdjustmentKey}` as FieldPath<PropertyGridForm>;

  const { control, watch } = useFormContext<PropertyGridForm>();
  const propertyData = watch(`columns.${columnIndex}.propertyData`);
  const manualAdjustmentOverride = watch(fieldName) as AloftPriceJSON | undefined;
  const propertyId = watch(`columns.${columnIndex}.propertyId`);

  const compAdjustments = useAppSelector(selectCompAdjustments)[propertyId];
  const adjustmentValue = getCompAdjustmentValue(manualAdjustmentKey, compAdjustments);

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedNewValue = newValue ? AloftPrice.fromDollarLike(newValue) : undefined;

    if (parsedNewValue?.equals(adjustmentValue)) return;

    const parsedNewValueJSON = parsedNewValue?.toJSON();
    onChange(parsedNewValueJSON);

    if (propertyData) handleUpdatePropertyData(propertyData, propertyId, watch(), dispatch);
  };

  const color = getAdjustmentColor(adjustmentValue);

  let isManuallyOverridden = false;
  if (manualAdjustmentOverride) {
    isManuallyOverridden = ['concessions', 'termsOfSale'].includes(manualAdjustmentKey)
      ? !schAloftPrice.parse(manualAdjustmentOverride).equals(AloftPrice.zero)
      : true;
  }

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { value, onChange, ref } }) => {
        const parsedValue = value ? schAloftPrice.parse(value) : undefined;
        const displayValue = (isManuallyOverridden ? parsedValue : adjustmentValue)?.toString(true);

        return (
          <Input
            ref={ref}
            type="text"
            value={displayValue}
            __dangerDoNotUseSx={{
              height: '100%',
              padding: '4px 8px',
              border: isManuallyOverridden
                ? `1px dashed ${theme.colors.buttonDefault}`
                : '1px solid transparent',
              outline: 'none',
              borderRadius: '0',
              fontSize: '14px',
              textAlign: 'right',
              backgroundColor: theme.colors.textOffset,
              color,
              _focusVisible: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
                background: theme.colors.surface3,
              },
              _hover: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
              },
            }}
            onBlur={() => handleSetFieldValue(onChange, displayValue)}
            onChange={(v) => onChange(AloftPrice.fromDollarLike(v).toJSON())}
            onClear={() => onChange(undefined)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                onChange(adjustmentValue.toString(true));
              } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
        );
      }}
    />
  );
};
